<template>
  <!--
    PoolaFestivalThermometer — the collective «أرباح أعضاء نادي السيلرز حتى الآن
    {amount}» live surface (festival P2-B). {amount} is the
    REAL cumulative collective-PoolShare total from
    meta.festival.collectivePoolshareUsedCents (the club's season-long PoolShare
    profit, NOT cash, NOT today-scoped) — never fabricated. The parent (PoolaRun)
    only mounts this once the club has actually accumulated PoolShare (> 0),
    matching the savings gate — so the celebratory opening moment never shows a
    hollow «0». The `v-if` guard here is defence-in-depth.

    The number rolls via PoolaOdometer on each polled/realtime change. A «مباشر»
    live flag + a gentle breathing glow signal liveness — both composite-only
    (opacity/transform) and reduced-motion gated.
  -->
  <div v-if="amountCents != null && amountCents > 0" class="poola-thermometer">
    <span class="poola-thermometer__flag">
      <span class="poola-thermometer__dot" aria-hidden="true" />
      {{ t('poola.festival.collective.liveFlag') }}
    </span>

    <i18n-t
      keypath="poola.festival.collective.poolshareSeason"
      tag="p"
      class="poola-thermometer__copy"
      scope="global"
    >
      <template #amount>
        <poola-odometer
          class="poola-thermometer__amount"
          :value="amountCents"
          :text="formatCurrency(amountCents)"
        ></poola-odometer>
      </template>
    </i18n-t>
  </div>
</template>

<script setup>
import { I18nT, useI18n } from 'vue-i18n';
import PoolaOdometer from './PoolaOdometer.vue';
import { useCurrency } from '@/composables/useCurrency.js';

defineProps({
  // meta.festival.collectivePoolshareUsedCents — the club's real cumulative
  // collective-PoolShare profit this season (all members, whole run, NOT cash,
  // NOT today). null/absent OR 0 → the surface hides; the live ticker only
  // appears once there's a real positive total to celebrate.
  amountCents: {
    type: Number,
    default: null,
  },
});

const { t } = useI18n({ useScope: 'global' });
const { formatCurrency } = useCurrency();
</script>

<style scoped>
.poola-thermometer {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 12px 16px;
  border-radius: var(--poolit-radius-lg);
  background: rgb(255 255 255 / 8%);
  overflow: hidden;
}

/* Composite-only breathing glow: a STATIC radial-gradient layer whose OPACITY
   breathes — never an animated box-shadow/color (those repaint). */
.poola-thermometer::before {
  content: '';
  position: absolute;
  inset: -40%;
  z-index: -1;
  background: radial-gradient(
    circle at 50% 30%,
    var(--poolshare-rate-gold-accent) 0%,
    transparent 62%
  );
  opacity: 0.35;
  animation: poola-thermometer-breathe 3.6s ease-in-out infinite;
  will-change: opacity, transform;
}

@keyframes poola-thermometer-breathe {
  0%,
  100% {
    opacity: 0.24;
    transform: scale(0.96);
  }

  50% {
    opacity: 0.46;
    transform: scale(1.04);
  }
}

.poola-thermometer__flag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: var(--poolit-radius-full);
  background: var(--poolit-error-100);
  color: var(--poolit-error-dark);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-bold);
  letter-spacing: 0.02em;
}

.poola-thermometer__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--poolit-error-dark);
  animation: poola-thermometer-pulse 1.6s ease-in-out infinite;
  will-change: opacity, transform;
}

@keyframes poola-thermometer-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(0.72);
  }
}

.poola-thermometer__copy {
  margin: 0;
  text-align: center;
  color: #fff;
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  line-height: var(--poolit-line-height-body);
}

.poola-thermometer__amount {
  /* Bright gold on the dark-purple festival card — the previous
     --poolshare-rate-gold-fg (#4d3000) is a dark coin-ink meant to sit ON a gold
     coin, so it read as near-black here. This accent gold is the hero figure, so
     it's a step larger than the copy with a soft dark halo for legibility over
     the animated glow behind it. */
  color: var(--poolshare-rate-gold-accent);
  font-size: var(--poolit-font-size-h2);
  font-weight: var(--poolit-font-weight-bold);
  letter-spacing: -0.01em;
  text-shadow: 0 1px 8px rgb(0 0 0 / 45%);
}

@media (prefers-reduced-motion: reduce) {
  .poola-thermometer::before,
  .poola-thermometer__dot {
    animation: none;
  }
}
</style>
